/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatTagLabel } from "../../utils";
import { CUSTOMER_STATUS_OPTIONS } from "./constants";

export const CustomerCRMEditForm = defineComponent({
  name: "CustomerCRMEditForm",
  props: {
    nameValue: { type: String, required: true },
    emailValue: { type: String, required: true },
    phoneValue: { type: String, required: true },
    birthDayMonthValue: { type: String, required: true },
    birthYearValue: { type: String, required: true },
    statusLevelValue: { type: String, required: true },
    tags: { type: Array as PropType<string[]>, required: true },
    newTagValue: { type: String, required: true },
    isSaving: { type: Boolean, required: true },
    onNameInput: { type: Function as PropType<(value: string) => void>, required: true },
    onEmailInput: { type: Function as PropType<(value: string) => void>, required: true },
    onPhoneInput: { type: Function as PropType<(value: string) => void>, required: true },
    onBirthDayMonthInput: { type: Function as PropType<(value: string) => void>, required: true },
    onBirthYearInput: { type: Function as PropType<(value: string) => void>, required: true },
    onStatusLevelInput: { type: Function as PropType<(value: string) => void>, required: true },
    onNewTagInput: { type: Function as PropType<(value: string) => void>, required: true },
    onAddTag: { type: Function as PropType<() => void>, required: true },
    onRemoveTag: { type: Function as PropType<(tag: string) => void>, required: true },
    onCancel: { type: Function as PropType<() => void>, required: true },
    onSave: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="space-y-4">
        <div class="bg-base-100/40 rounded-xl p-5 border border-base-content/5 space-y-4">
          <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider">
            {t("reservations.customers.edit_customer")}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px]">{t("reservations.customers.name")}</span>
              </div>
              <input
                type="text"
                class="input input-bordered input-sm bg-base-100 text-sm"
                value={props.nameValue}
                onInput={(e) => props.onNameInput((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px]">{t("reservations.customers.email")}</span>
              </div>
              <input
                type="email"
                class="input input-bordered input-sm bg-base-100 text-sm"
                value={props.emailValue}
                onInput={(e) => props.onEmailInput((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px]">{t("reservations.customers.phone")}</span>
              </div>
              <input
                type="tel"
                class="input input-bordered input-sm bg-base-100 text-sm"
                value={props.phoneValue}
                onInput={(e) => props.onPhoneInput((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px]">{t("reservations.customers.status_level")}</span>
              </div>
              <select
                class="select select-bordered select-sm bg-base-100 text-sm"
                value={props.statusLevelValue}
                onChange={(e) => props.onStatusLevelInput((e.target as HTMLSelectElement).value)}
              >
                <option value="">{t("reservations.customers.status_not_set")}</option>
                {CUSTOMER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px] flex items-center gap-1">
                  <span class="material-symbols-outlined text-[12px]">cake</span>
                  {t("reservations.customers.birth_day_month")}
                </span>
              </div>
              <input
                type="text"
                inputmode="numeric"
                placeholder="MM-DD"
                class="input input-bordered input-sm bg-base-100 text-sm"
                value={props.birthDayMonthValue}
                onInput={(e) => props.onBirthDayMonthInput((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control">
              <div class="label py-0.5">
                <span class="label-text text-[11px]">{t("reservations.customers.birth_year")}</span>
              </div>
              <input
                type="number"
                min="1900"
                max="2100"
                placeholder="YYYY"
                class="input input-bordered input-sm bg-base-100 text-sm"
                value={props.birthYearValue}
                onInput={(e) => props.onBirthYearInput((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>

          <div>
            <div class="label py-0.5">
              <span class="label-text text-[11px]">{t("reservations.customers.tags")}</span>
            </div>
            <div class="flex flex-wrap gap-1.5 items-center">
              {props.tags.map((tag) => (
                <span key={tag} class="badge badge-sm bg-base-200 gap-1 pr-0.5">
                  {formatTagLabel(tag)}
                  <button
                    onClick={() => props.onRemoveTag(tag)}
                    class="btn btn-xs btn-circle btn-ghost h-3.5 w-3.5 min-h-0"
                  >
                    <span class="material-symbols-outlined text-[10px]">close</span>
                  </button>
                </span>
              ))}
              <div class="flex items-center gap-0.5">
                <input
                  type="text"
                  class="input input-bordered input-xs w-24 bg-base-100 text-[11px]"
                  placeholder={t("reservations.customers.add_tag_placeholder")}
                  value={props.newTagValue}
                  onInput={(e) => props.onNewTagInput((e.target as HTMLInputElement).value)}
                  onKeydown={(e: KeyboardEvent) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      props.onAddTag();
                    }
                  }}
                />
                <button
                  onClick={props.onAddTag}
                  class="btn btn-xs btn-circle btn-ghost"
                  disabled={!props.newTagValue.trim()}
                >
                  <span class="material-symbols-outlined text-[12px]">add</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <button onClick={props.onCancel} class="btn btn-sm btn-ghost">
            {t("reservations.customers.cancel")}
          </button>
          <button onClick={props.onSave} class="btn btn-sm btn-primary gap-1" disabled={props.isSaving}>
            {props.isSaving ? (
              <span class="loading loading-spinner loading-xs" />
            ) : (
              <span class="material-symbols-outlined text-[14px]">save</span>
            )}
            {t("reservations.customers.save")}
          </button>
        </div>
      </div>
    );
  },
});
